import { ReactElement, useEffect, useState } from 'react';
import { Button, Text, VStack, Box, useToken } from '@chakra-ui/react';
import { FieldError } from 'react-hook-form';
import {
  FeedbackMessage,
  FeedbackOption,
  FeedbackType,
  ShortFeedbackMessage,
} from '@novaheal/types';
import { Slider } from 'components/atoms/Slider';
import { Z_INDEX } from 'styles/zIndex';
import { TextField } from '../Form';
import { Form } from '../Form/v2';

type FeedbackInputType = {
  message: string;
};

type Props = {
  onSubmit: ({ message, shortMessages }: FeedbackMessage) => Promise<void>;
  feedbackType: FeedbackType;
};

const options: FeedbackOption[] = [
  {
    value: ShortFeedbackMessage.SUPER,
    label: '👍 Super',
  },
  {
    value: ShortFeedbackMessage.DUMB,
    label: '👎 Blöd',
  },
  {
    value: ShortFeedbackMessage.HELPFUL,
    label: '🤩 Hilfreich',
  },
  {
    value: ShortFeedbackMessage.NOT_ENOUGH,
    label: '❓ Zu oberflächlich',
  },
  {
    value: ShortFeedbackMessage.BORING,
    label: '😴 zu langweilig',
  },
  {
    value: ShortFeedbackMessage.DONT_UNDERSTAND,
    label: '🙈 Unverständlich',
  },

  {
    value: ShortFeedbackMessage.NOT_PRACTICAL_ENOUGH,
    label: '🩹 Zu wenig praktisch',
  },
  {
    value: ShortFeedbackMessage.NOT_RELEVANT_ANYMORE,
    label: '🕰️ Veraltet',
  },
  {
    value: ShortFeedbackMessage.PLEASE_ADD_VIDEO,
    label: '🎥 Erklärvideo gewünscht',
  },
];

export const FeedbackInput = ({ onSubmit, feedbackType }: Props): ReactElement => {
  const [selectedOptions, setSelectedOptions] = useState<FeedbackOption[]>([]);
  const [bgLight] = useToken('colors', ['background.primary']);

  useEffect(() => {
    if (feedbackType === FeedbackType.POSITIVE) {
      setSelectedOptions([options[0]]);
    } else {
      setSelectedOptions([options[1]]);
    }
  }, [feedbackType]);

  const handleOptionClick = (label: string, value: ShortFeedbackMessage): void => {
    setSelectedOptions([...selectedOptions, { label, value }]);
  };

  const handleRemoveOptionClick = (value: ShortFeedbackMessage): void => {
    const tmp = selectedOptions;

    const filtered = tmp.filter((option) => option.value !== value);
    setSelectedOptions(filtered);
  };

  const handleSubmit = async (values: { message: string }): Promise<void> => {
    await onSubmit({
      message: values.message,
      shortMessages: selectedOptions.map((option) => option.value),
    });
  };

  const filteredOptions = options.filter((option) =>
    selectedOptions.every((selectedOption) => selectedOption.value !== option.value)
  );

  const emojiSlides = filteredOptions.map((option) => {
    return (
      <Box
        bg="tag.background.green"
        borderRadius="5px"
        textStyle="body.s.regular"
        px="4"
        py="2"
        display="inline-block"
        mr="4"
        cursor="pointer"
        onClick={() => handleOptionClick(option.label, option.value)}
      >
        {option.label}
      </Box>
    );
  });

  const selectedEmojiSlides = selectedOptions.map((option) => (
    <Box
      bg="tag.background.green"
      borderRadius="5px"
      textStyle="body.s.regular"
      display="inline-block"
      px="4"
      py="2"
      mr="4"
      cursor="pointer"
      onClick={() => handleRemoveOptionClick(option.value)}
    >
      {option.label}
    </Box>
  ));

  return (
    <Form<FeedbackInputType> onSubmit={handleSubmit} autoComplete="off">
      {({ register, formState }) => (
        <VStack gap="14px">
          <Text textStyle="body.m.regular" textAlign="center" color="text.tertiary">
            Deine Bewertung und dein Feedback helfen uns dabei, Lerninhalte aktuell und leicht
            verständlich zu gestalten!
          </Text>
          <Box h="35px">
            <Box
              whiteSpace="nowrap"
              overflow="auto"
              position="absolute"
              left={{ base: '5', md: '8' }}
              width="100%"
              w={{
                base: 'calc(100% - var(--chakra-spacing-5))',
                md: 'calc(100% - var(--chakra-spacing-8))',
              }}
            >
              <Box
                w="80px"
                h="full"
                pointerEvents="none"
                bg={`linear-gradient(to right, rgba(255,255,255,0), ${bgLight} 50%)`}
                position="absolute"
                zIndex={Z_INDEX.BASE}
                right="0"
              />
              <Slider slides={emojiSlides} containerPadding="0" />
            </Box>
          </Box>
          <Box width="100%">
            <TextField
              error={formState.errors.message as FieldError}
              registration={register('message')}
              placeholder="Schreib uns dein Feedback hier…"
            />
            <Box
              width="100%"
              h="55px"
              border="default"
              borderTop="none"
              borderColor="border.accent"
              mt="0"
              borderBottomRadius="4px"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px="2"
            >
              <Box
                whiteSpace="nowrap"
                overflow="auto"
                position="absolute"
                left={{ base: '6', md: '9' }}
                w={{
                  base: 'calc(100% - var(--chakra-spacing-5))',
                  md: 'calc(100% - var(--chakra-spacing-8) - var(--chakra-spacing-9) - 1px)',
                }}
              >
                <Slider slides={selectedEmojiSlides} containerPadding="0" />
              </Box>
            </Box>
          </Box>
          <Button
            type="submit"
            variant="primary"
            w="full"
            disabled={!formState.isValid || selectedOptions.length === 0}
          >
            Senden
          </Button>
        </VStack>
      )}
    </Form>
  );
};
